/**
 * The one status vocabulary of this app (status-as-text 1.13).
 *
 * A state is a word and a tone, and the tone is the semantic *text* colour beside the word,
 * never a fill and never orange. A plan running normally is neutral: Active is success only
 * because it is the state a client wants confirmed, and Canceled is neutral because the client
 * decided it and the app does not editorialise about it (DS18, subscription status).
 *
 * A state the vocabulary does not carry returns null, and the caller draws nothing rather than
 * printing Stripe's own English into a French page.
 */

export type StatusTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface StatusWord {
  /** The i18n key of the word; the caller runs it through t(). */
  key: string;
  tone: StatusTone;
}

const INVOICE: Record<string, StatusWord> = {
  open: { key: "gymkeePay.common.statusPending", tone: "neutral" },
  paid: { key: "gymkeePay.common.statusPaid", tone: "success" },
  void: { key: "gymkeePay.common.statusCanceled", tone: "neutral" },
  uncollectible: {
    key: "gymkeePay.common.statusUncollectible",
    tone: "danger",
  },
  draft: { key: "gymkeePay.common.statusDraft", tone: "neutral" },
};

const SUBSCRIPTION: Record<string, StatusWord> = {
  active: { key: "gymkeePay.common.statusActive", tone: "success" },
  trialing: { key: "gymkeePay.common.statusTrial", tone: "neutral" },
  past_due: { key: "gymkeePay.common.statusPastDue", tone: "danger" },
  unpaid: { key: "gymkeePay.common.statusUnpaid", tone: "danger" },
  incomplete: { key: "gymkeePay.common.statusIncomplete", tone: "warning" },
  incomplete_expired: {
    key: "gymkeePay.common.statusIncomplete",
    tone: "warning",
  },
  canceled: { key: "gymkeePay.common.statusCanceled", tone: "neutral" },
  paused: { key: "gymkeePay.common.statusPaused", tone: "neutral" },
};

export function invoiceStatus(status: string | undefined): StatusWord | null {
  if (!status) return null;
  return INVOICE[status] ?? null;
}

export function subscriptionStatus(
  status: string | undefined,
): StatusWord | null {
  if (!status) return null;
  return SUBSCRIPTION[status] ?? null;
}
